import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { Subscription } from 'rxjs';

import { SharedModule } from '../../_shared/shared.module';
import { AbilityService } from '../../_shared/services/ability.service';
import { DataService } from '../../_shared/services/data.service';
import { SessionService } from '../../_shared/services/session.service';
import { UtilService } from '../../_shared/services/util.service';

import { OccupancyType } from '../shared/occupancy-type.model';
import { OccupancyTypeService } from '../shared/occupancy-type.service';

@Component({
  selector: 'app-occupancy-type-list',
  templateUrl: './occupancy-type-list.component.html',
  standalone: true,
  imports: [SharedModule],
})
export class OccupancyTypeListComponent implements OnInit, OnDestroy {
  @Input() occupancyTypes: OccupancyType[];
  @Input() meta;
  @Input() loadingOccupancyTypes: boolean;
  @Input() defaultLanguage;

  @Output() private selectedOccupancyType = new EventEmitter<{
    occupancyType: OccupancyType;
    mode: string;
  }>();

  permissions = {
    canShow: this.abilityService.can('show_occupancytype_accountadmin'),
    canUpdate: this.abilityService.can('update_occupancytype_accountadmin'),
    canDestroy: this.abilityService.can('destroy_occupancytype_accountadmin'),
  };

  filterCriteria = this.occupancyTypeService.getFilterCriteria();

  masterCheckboxSelected = false;
  showListActions = false;
  totalItemsSelected = 0;

  private $subs: Subscription;

  constructor(
    private occupancyTypeService: OccupancyTypeService,
    private abilityService: AbilityService,
    private dataService: DataService,
    private sessionService: SessionService,
    public utilService: UtilService
  ) {}

  ngOnInit() {
    this.$subs = this.utilService.getCheckboxsSubjectValue().subscribe((data) => {
      this.showListActions = data.showListActions;
      this.totalItemsSelected = data.totalItemsSelected;
      this.masterCheckboxSelected = data.masterCheckboxSelected;
    });
  }

  ngOnDestroy() {
    if (this.$subs) {
      this.$subs.unsubscribe();
    }
  }

  reloadOccupancyTypes() {
    this.filterCriteria = this.occupancyTypeService.getFilterCriteria();

    this.getOccupancyTypes();
  }

  // Referenced from app-search-text-input
  onSearched({ field, value }) {
    this.filterOccupancyTypes(field, value);
  }

  filterOccupancyTypes(field, value) {
    this.filterCriteria = this.utilService.setFilterCriteria(this.filterCriteria, field, value);

    this.getOccupancyTypes();
  }

  // Referenced from app-sort-by
  onSorted({ sortBy, sortDir }) {
    this.filterCriteria.page = 1;
    this.filterCriteria.sortDir = sortDir;
    this.filterCriteria.sortedBy = sortBy;

    this.getOccupancyTypes();
  }

  // Referenced from app-table-footer
  onSelectedPage({ page, offset }) {
    this.filterCriteria.page = page;
    this.filterCriteria.offset = offset;

    this.getOccupancyTypes();
  }

  selectOccupancyType(occupancyType: OccupancyType, mode = '') {
    if (!this.permissions.canShow) return;

    this.selectedOccupancyType.emit({ occupancyType, mode });
  }

  // Referenced from app-list-actions
  onUpdatedItems(option) {
    let successCallback = () => {
      this.getOccupancyTypes();
    };

    this.dataService.changeItemsStatus(
      'occupancy_types',
      this.occupancyTypes,
      option,
      successCallback
    );
  }

  // Referenced from app-list-actions
  onDeletedItems() {
    let successCallback = () => {
      this.getOccupancyTypes();
    };

    this.dataService.deleteItems('occupancy_types', this.occupancyTypes, successCallback);
  }

  changeOccupancyTypeStatus(occupancyType: OccupancyType) {
    let successCallback = () => {
      this.utilService.changeItemRowStyle(occupancyType.id);
    };

    this.dataService.changeItemStatus('occupancy_types', occupancyType, successCallback, {});
  }

  deleteOccupancyType(occupancyType: OccupancyType) {
    let successCallback = () => {
      this.getOccupancyTypes();
    };

    const params = {
      title: 'DELETE_OCCUPANCY_TYPE',
      // message: 'SURE_TO_DELETE_OCCUPANCY_TYPE',
      reason: 'DELETE_ITEM_REASON',
      requireConfirmation: true,
      successMsg: 'DELETED_OCCUPANCY_TYPE',
    };

    this.dataService.deleteItem('occupancyTypes', occupancyType, successCallback, params);
  }

  private getOccupancyTypes() {
    this.filterCriteria.lang = this.defaultLanguage.lang;

    this.loadingOccupancyTypes = true;
    this.occupancyTypeService.getOccupancyTypes(this.filterCriteria).subscribe((response) => {
      this.occupancyTypes = response.data;
      this.meta = response.meta;

      this.loadingOccupancyTypes = false;
    });
  }
}
